/**
 * MONSTER TOWN — 3D PIECE THUMBNAILS. HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * WHY THIS EXISTS (fixed r3). The shop sold flat vector drawings of things the
 * world renders as chunky 3D toys: "the flat vector item icons do not resemble
 * the chunky 3D objects they sell". A pre-reader buys with their eyes and
 * nothing else — if the picture on the card is not the thing that lands in
 * their town, the card is a promise the game does not keep, and there is no
 * text anywhere to correct it.
 *
 * The contract has always said the default icon is "an auto-rendered 3D
 * thumbnail" (§5). This is that renderer, and it is deliberately the SAME
 * `buildArt` the world calls, at the SAME camera angle as the game — so the
 * picture on the card is the object, drawn by the object's own code. A new
 * furniture set gets a correct shop picture for free, which is the litmus in
 * the brief: content is data, never an engine change.
 *
 * HOW IT STAYS CHEAP ON THE HAND-ME-DOWN:
 *  - ONE small offscreen WebGL context, created on first use, never per card.
 *  - Every thumbnail is rendered ONCE and cached as an ImageBitmap-free plain
 *    canvas keyed by def id + size, so paging the shelf back and forth and
 *    reopening the panel cost nothing at all.
 *  - The context is released a few seconds after the last thumbnail, because an
 *    older iPad has a small budget of live WebGL contexts and the game's own is
 *    the one that matters.
 *  - `ghost: true` in the art context, exactly as the contract prescribes for a
 *    thumbnail: props that have an expensive full-fat form draw their cheap one.
 *
 * AND IT CAN ALWAYS FAIL SAFELY. If a context cannot be had — old hardware,
 * too many contexts, a headless quirk — `drawPieceThumb` returns false and the
 * caller falls back to the flat icon. A shop that cannot draw a picture is a
 * shop with no pictures; that is not allowed to happen.
 */

import * as THREE from 'three'
import { Grid, type Cell } from '../core/grid'
import { hex } from '../core/palette'
import type { AnyPlaceableDef } from '../core/placeable'

/** Matches the game camera: 45-degree yaw, 34-degree pitch (contract §11). */
const YAW = Math.PI / 4
const PITCH = (34 * Math.PI) / 180
/** Drawn at this many device px regardless of the card, then scaled to fit. */
const RENDER_PX = 256
/** Release the context this long after the last thumbnail was asked for. */
const IDLE_MS = 4000

let renderer: THREE.WebGLRenderer | null = null
let scene: THREE.Scene | null = null
let camera: THREE.OrthographicCamera | null = null
let failed = false
let idleTimer = 0

const cache = new Map<string, HTMLCanvasElement>()
/** One grid per scale, reused: `buildArt` only ever reads from it. */
const grids = new Map<string, Grid>()

const ANCHOR: Cell = { gx: 1, gy: 0, gz: 1 }

function gridFor(scale: 'exterior' | 'interior'): Grid {
  const found = grids.get(scale)
  if (found !== undefined) return found
  const made = new Grid({ id: `thumb-${scale}`, scale, width: 4, depth: 4, levels: 3 })
  grids.set(scale, made)
  return made
}

function boot(): boolean {
  if (failed) return false
  if (renderer !== null) return true
  try {
    const canvas = document.createElement('canvas')
    canvas.width = RENDER_PX
    canvas.height = RENDER_PX
    const r = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      // The card blits this canvas into its own 2D canvas, which reads the
      // drawing buffer after the render call returns. Without this the read
      // comes back empty on most drivers.
      preserveDrawingBuffer: true,
      powerPreference: 'low-power',
      stencil: false,
      depth: true,
    })
    r.setClearColor(0x000000, 0)
    r.setSize(RENDER_PX, RENDER_PX, false)
    r.outputColorSpace = THREE.SRGBColorSpace
    // No shadow map: a thumbnail is 256px and the shadow pass is the expensive
    // half of a render this small.
    r.shadowMap.enabled = false

    const s = new THREE.Scene()
    // The same two lights the world uses, at the same relative angle, so a
    // piece is lit on the card the way it will be lit in the town.
    s.add(new THREE.HemisphereLight(hex('skyTop'), hex('grassLight'), 1.15))
    const sun = new THREE.DirectionalLight(hex('sunWarm'), 1.5)
    sun.position.set(14, 24, 10)
    s.add(sun)

    renderer = r
    scene = s
    camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 200)
    return true
  } catch {
    failed = true
    return false
  }
}

function release(): void {
  if (renderer === null) return
  renderer.dispose()
  renderer.forceContextLoss?.()
  renderer = null
  scene = null
  camera = null
}

function touchIdle(): void {
  if (idleTimer !== 0) window.clearTimeout(idleTimer)
  idleTimer = window.setTimeout(() => {
    idleTimer = 0
    release()
  }, IDLE_MS)
}

/** Throw away every mesh in `root` that this module created. Kit geometry and
 *  kit materials are SHARED and cached — disposing them would blank the town. */
function drop(root: THREE.Object3D): void {
  root.removeFromParent()
}

function render(def: AnyPlaceableDef): HTMLCanvasElement | null {
  if (!boot() || renderer === null || scene === null || camera === null) return null
  const grid = gridFor(def.scale === 'both' ? 'exterior' : def.scale)
  let art: THREE.Object3D
  try {
    art = def.buildArt({
      def,
      instance: null,
      grid,
      anchor: ANCHOR,
      rot: 0,
      connect: 0,
      connect8: 0,
      tint: def.tints?.[0] ?? null,
      variety: 0.5,
      ghost: true,
    })
  } catch {
    return null
  }
  scene.add(art)

  // FIT THE PIECE, WHATEVER SIZE IT IS. A 1x1 lamp and a 3x2 bed both have to
  // fill the same card, so the frustum comes from the art's own bounds.
  const box = new THREE.Box3().setFromObject(art)
  if (box.isEmpty()) {
    drop(art)
    return null
  }
  const centre = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())
  const radius = Math.max(0.4, Math.hypot(size.x, size.y, size.z) * 0.5)

  const dir = new THREE.Vector3(
    Math.cos(PITCH) * Math.sin(YAW),
    Math.sin(PITCH),
    Math.cos(PITCH) * Math.cos(YAW),
  )
  /*
   * FIT WHAT THE CAMERA WILL ACTUALLY SEE, not the bounding sphere.
   *
   * A sphere fit is the same size for a lamp post and for a bed, so a tall thin
   * piece came out as a matchstick in the middle of a big empty card and a kid
   * could not tell what it was. Project the eight corners onto the camera's own
   * right/up axes and take the real half-extents: every piece then fills its
   * card as much as its own shape allows.
   */
  const right = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), dir).normalize()
  const up = new THREE.Vector3().crossVectors(dir, right).normalize()
  let halfX = 0.2
  let halfY = 0.2
  const corner = new THREE.Vector3()
  for (let i = 0; i < 8; i++) {
    corner.set(
      (i & 1) === 0 ? box.min.x : box.max.x,
      (i & 2) === 0 ? box.min.y : box.max.y,
      (i & 4) === 0 ? box.min.z : box.max.z,
    )
    corner.sub(centre)
    halfX = Math.max(halfX, Math.abs(corner.dot(right)))
    halfY = Math.max(halfY, Math.abs(corner.dot(up)))
  }
  // Square frustum: a card is square, and a piece that keeps its aspect ratio
  // is a piece a kid can compare with the one next to it.
  const half = Math.max(halfX, halfY) * 1.1
  camera.left = -half
  camera.right = half
  camera.top = half
  camera.bottom = -half
  camera.near = 0.1
  camera.far = radius * 8 + 20
  camera.position.copy(centre).addScaledVector(dir, radius * 4 + 6)
  camera.lookAt(centre)
  camera.updateProjectionMatrix()

  renderer.render(scene, camera)
  drop(art)

  // Copy out immediately: the next thumbnail overwrites the drawing buffer.
  const out = document.createElement('canvas')
  out.width = RENDER_PX
  out.height = RENDER_PX
  const c = out.getContext('2d')
  if (c === null) return null
  c.drawImage(renderer.domElement, 0, 0)
  return out
}

/**
 * Draw `def` as the 3D object it actually is, into a 2D card canvas.
 * Returns false if there is no WebGL to do it with — draw the flat icon then.
 */
export function drawPieceThumb(
  c: CanvasRenderingContext2D,
  size: number,
  def: AnyPlaceableDef,
): boolean {
  const key = String(def.id)
  let pic = cache.get(key)
  if (pic === undefined) {
    const made = render(def)
    if (made === null) return false
    cache.set(key, made)
    pic = made
  }
  touchIdle()
  c.clearRect(0, 0, size, size)
  c.drawImage(pic, 0, 0, size, size)
  return true
}

/** QA/harness: how many thumbnails have been rendered so far. */
export function thumbCacheSize(): number {
  return cache.size
}
